import wrap from "word-wrap";

import { ScaleInfo, TextAnnotation } from "custom-models/ViewerTypes";

import { AnnotationColors } from "./subtree";
import ViewerFacade from "../ViewerFacade";

// Also in Measurement.tsx; to be replaced with something more robust later
const EXPECTED_CHAR_WIDTH = 16;
const EXPECTED_CHAR_HEIGHT = 20;

const FONT_SIZE = 14;

interface TextProps {
  annotation: TextAnnotation;
  scaleInfo: ScaleInfo;
  viewerFacade: ViewerFacade;
  isSelected?: boolean;
}

const textWidths = new Map<String, number>();

function expectedTextWidth(text: string, fontsize: number) {
  const fudgeFactor = 1.15;
  const width = textWidths.get(text);
  if (width) {
    return width;
  }
  const div = document.createElement("div");
  div.style.position = "absolute";
  div.style.width = "auto";
  div.style.height = "auto";
  div.style.whiteSpace = "nowrap";
  div.style.fontSize = `${fontsize}`;
  div.innerHTML = text;
  document.body.appendChild(div);
  const offsetWidth = Math.floor(div.offsetWidth * fudgeFactor);
  document.body.removeChild(div);
  textWidths.set(text, offsetWidth);
  return offsetWidth;
}

const Text = (props: TextProps) => {
  const { scaleInfo, annotation } = props;
  const { point, label } = annotation;

  if (!point) {
    return <></>;
  }

  const lineLength = 100;
  const color = props.isSelected
    ? AnnotationColors.SELECTED
    : annotation.id
    ? AnnotationColors.DEFAULT
    : AnnotationColors.ACTIVE;

  const lines = wrap(label || "", { width: 40 })
    .split(/[\r\n]/)
    .map((s: string) => s.trim());
  let scale = scaleInfo.scaleX;
  const fontsize = Math.floor(FONT_SIZE / scale);
  const maxWidthPixels = lines.reduce((acc: number, e: string) => {
    return Math.max(acc, expectedTextWidth(e, fontsize));
  }, 0);

  let east: boolean = point.x < scaleInfo.width / 2;
  let south: boolean = point.y < scaleInfo.height / 2;
  const rot = scaleInfo.rotation;
  const swapAngle = 60;
  if (rot > swapAngle && rot < 180 - swapAngle) {
    const swap = south;
    south = east;
    east = !swap;
  }
  if (rot >= 180 - swapAngle && rot <= 180 + swapAngle) {
    south = !south;
    east = !east;
  }
  if (rot > 180 + swapAngle && rot < 360 - swapAngle) {
    const swap = south;
    south = !east;
    east = swap;
  }
  const margin = EXPECTED_CHAR_WIDTH / scale / 2;
  const burrLength = lineLength / scale / 10;

  return (
    <g
      x={point.x}
      y={point.y}
      transform={`rotate(${-scaleInfo.rotation} ${point.x} ${point.y})`}
    >
      <line
        key="arrow-main"
        x1={point.x}
        y1={point.y}
        x2={
          point.x +
          (east ? lineLength / scale - margin : -lineLength / scale + margin)
        }
        y2={
          point.y +
          (south ? lineLength / scale - margin : -lineLength / scale + margin)
        }
        strokeWidth={2.0 / scale}
        stroke={color}
      />

      <line
        key="arrow-left-burr"
        x1={point.x}
        y1={point.y}
        x2={point.x + (east ? burrLength : -burrLength)}
        y2={point.y + (south ? burrLength : -burrLength)}
        strokeWidth={2.0 / scale}
        stroke={color}
        transform={`rotate(30 ${point.x} ${point.y})`}
      />

      <line
        key="arrow-right-burr"
        x1={point.x}
        y1={point.y}
        x2={point.x + (east ? burrLength : -burrLength)}
        y2={point.y + (south ? burrLength : -burrLength)}
        strokeWidth={2.0 / scale}
        stroke={color}
        transform={`rotate(-30 ${point.x} ${point.y})`}
      />

      <rect
        key="rect"
        x={
          point.x +
          (east
            ? lineLength / scale - margin
            : -lineLength / scale - margin - maxWidthPixels / scale)
        }
        y={
          point.y +
          (south
            ? lineLength / scale - margin
            : -lineLength / scale -
              margin -
              (lines.length * EXPECTED_CHAR_HEIGHT) / scale)
        }
        width={2 * margin + maxWidthPixels / scale}
        height={2 * margin + (EXPECTED_CHAR_HEIGHT * lines.length) / scale}
        stroke={color}
        strokeWidth={2.0 / scale}
        fill="rgba(240, 240, 240, 0.85)"
      />
      {lines.map((line: string, index: number) => (
        <text
          key={"text-line-" + index}
          x={
            point.x +
            (east
              ? lineLength / scale
              : -lineLength / scale - maxWidthPixels / scale)
          }
          y={
            point.y +
            (south
              ? lineLength / scale
              : -lineLength / scale -
                (lines.length * EXPECTED_CHAR_HEIGHT) / scale) +
            (EXPECTED_CHAR_HEIGHT * (1 + index)) / scale
          }
          textAnchor="start"
          style={{ fontSize: `${fontsize}pt` }}
          fill={color}
        >
          {line}
        </text>
      ))}
    </g>
  );
};

export default Text;
